/**
 * ChannelThumbnailRefsSection — upload a small library of "aspiration"
 * thumbnail images (typically the user's best manual ChatGPT-generated
 * versions). These are passed to gpt-image-1 as STYLE references on
 * every future thumbnail generation for videos on this channel, so
 * the app's output locks onto the user's proven aesthetic.
 *
 * Why per-channel rather than per-storyboard: the user was explicit —
 * "I don't want to have to upload a reference thumbnail example for
 * every story animation." Upload once here, applies everywhere on
 * the channel.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  listChannelThumbnailRefs,
  uploadChannelThumbnailRef,
  deleteChannelThumbnailRef,
  channelThumbnailRefUrl,
  backfillChannelThumbnailRefProfiles,
} from "@/lib/api";

export default function ChannelThumbnailRefsSection({ jobId }) {
  const [refs, setRefs] = useState([]);
  const [maxRefs, setMaxRefs] = useState(4);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listChannelThumbnailRefs(jobId);
      setRefs(r?.refs || []);
      if (typeof r?.max_per_channel === "number") setMaxRefs(r.max_per_channel);
    } catch (err) {
      // Silent — the panel just shows empty if the API isn't reachable.
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const pick = () => {
    if (uploading || refs.length >= maxRefs) return;
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$|image\/(png|jpeg|webp)/i.test(file.name + " " + file.type)) {
      toast.error("Only PNG / JPEG / WEBP images are accepted");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File is over 20 MB");
      return;
    }
    setUploading(true);
    try {
      await uploadChannelThumbnailRef(jobId, file);
      toast.success("Reference thumbnail added");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload reference");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (refId) => {
    if (!window.confirm("Remove this reference thumbnail?")) return;
    try {
      await deleteChannelThumbnailRef(jobId, refId);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const runBackfill = async (force = false) => {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const res = await backfillChannelThumbnailRefProfiles(jobId, { force });
      const n = res?.updated || 0;
      if (n > 0) {
        toast.success(
          force
            ? `Re-extracted style DNA on ${n} reference${n === 1 ? "" : "s"} using the latest extractor`
            : `Extracted style DNA from ${n} reference${n === 1 ? "" : "s"}`,
        );
      } else if (force) {
        toast.success("No references to re-extract");
      } else {
        toast.success("All references already analysed — click Re-extract to force refresh");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't extract style profiles");
    } finally {
      setBackfilling(false);
    }
  };

  const atCap = refs.length >= maxRefs;
  const missingProfiles = refs.filter((r) => !r.style_profile).length;

  return (
    <section className="mb-8" data-testid="ck-thumb-refs-section">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
        05 · Reference Thumbnails
      </div>
      <p className="text-xs text-zinc-400 mb-4 max-w-2xl leading-relaxed">
        Upload examples of thumbnails you want the app to match — typically
        your best manual ChatGPT versions. On upload we extract a
        <span className="text-yellow-400"> style DNA</span> (colours, layout,
        character type, density) via vision LLM and feed that DNA into
        every future thumbnail on this channel — WITHOUT passing the
        reference image itself as a pixel input. That way the app
        adopts your style but writes fresh, topic-matched content per
        video (no more "every thumbnail looks like the reference"). PNG /
        JPEG / WEBP, up to 20 MB each, max {maxRefs} per channel.
      </p>
      {(missingProfiles > 0 || refs.length > 0) && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => runBackfill(false)}
            disabled={backfilling || loading || missingProfiles === 0}
            data-testid="ck-thumb-ref-backfill"
            className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm border-2 border-yellow-400 text-yellow-300 hover:bg-yellow-400 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Extract style DNA on refs that don't have one yet"
          >
            {backfilling ? "Extracting…" : "↻ Extract style DNA"}
          </button>
          {refs.length > 0 && (
            <button
              type="button"
              onClick={() => runBackfill(true)}
              disabled={backfilling || loading}
              data-testid="ck-thumb-ref-reextract"
              className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm border-2 border-emerald-400 text-emerald-300 hover:bg-emerald-400 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Force re-extract on ALL refs — use this after we ship a stronger extractor prompt"
            >
              {backfilling ? "Re-extracting…" : "⟲ Re-extract all (force)"}
            </button>
          )}
          {missingProfiles > 0 && (
            <span
              data-testid="ck-thumb-ref-missing-count"
              className="font-mono text-[10px] uppercase tracking-widest text-amber-400"
            >
              {missingProfiles} ref{missingProfiles === 1 ? "" : "s"} still missing style DNA
            </span>
          )}
          {missingProfiles === 0 && refs.length > 0 && (
            <span
              data-testid="ck-thumb-ref-profiles-ready"
              className="font-mono text-[10px] uppercase tracking-widest text-emerald-400"
            >
              ✓ style DNA ready for all references
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {refs.map((r) => (
          <div
            key={r.ref_id}
            className="relative group border border-zinc-800 bg-zinc-950/50 rounded-sm overflow-hidden aspect-video"
            data-testid={`ck-thumb-ref-${r.ref_id}`}
          >
            <img
              src={channelThumbnailRefUrl(r.preview_url)}
              alt="Reference thumbnail"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <button
              type="button"
              onClick={() => remove(r.ref_id)}
              data-testid={`ck-thumb-ref-remove-${r.ref_id}`}
              className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[10px] uppercase tracking-widest text-red-300 bg-black/80 border border-red-500/60 px-2 py-1 rounded-sm hover:bg-red-500/20"
            >
              ✕ remove
            </button>
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-zinc-300">
              {(r.size_bytes / 1024 / 1024).toFixed(1)} MB
            </div>
          </div>
        ))}
        {!atCap && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading || loading}
            data-testid="ck-thumb-ref-upload"
            className="aspect-video border border-dashed border-zinc-800 hover:border-yellow-400/60 hover:text-yellow-300 bg-zinc-950 rounded-sm flex flex-col items-center justify-center gap-2 p-6 transition-colors disabled:opacity-50"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">
              {uploading ? "uploading…" : "+ add reference"}
            </span>
            <span className="text-[10px] text-zinc-600 font-mono">
              png / jpg / webp
            </span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="ck-thumb-ref-input"
      />
      {atCap && (
        <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          At channel cap ({maxRefs}). Remove one to upload another.
        </p>
      )}
    </section>
  );
}
